"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import { auth } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";
import { getStoredAuthToken } from "./auth";
import { PLATFORM_NAME } from "./constants";
import { useEffectOnce } from "./useEffectOnce";
import { useRouter } from "next/navigation";

// Firebase client SDK automatically persists session using localStorage in the browser.
// However, for cross-subdomain access, we also restore from cookies.

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  loginWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // Helper to read and clear a cookie
  const readCookie = (name: string): string | null => {
    if (typeof document === "undefined") return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
  };

  const clearCookie = (name: string) => {
    if (typeof window === "undefined") return;
    const host = window.location.host;
    let domain = host;
    if (host.includes(`${PLATFORM_NAME}.com`)) {
      domain = `.${PLATFORM_NAME}.com`;
    } else {
      const hostParts = host.split(".");
      domain =
        hostParts.length >= 2 ? `.${hostParts.slice(-2).join(".")}` : host;
    }
    document.cookie = `${name}=; path=/; domain=${domain}; max-age=0; SameSite=Lax`;
  };

  // Helper function to restore auth from stored cookie token
  const restoreAuthFromCookie = async () => {
    console.log("🍪 [useAuth] Checking for stored auth token...");

    const storedToken = getStoredAuthToken();
    if (storedToken && !auth.currentUser) {
      console.log(
        "🔐 [useAuth] Found stored token, attempting to restore Firebase auth..."
      );
      try {
        // Note: signInWithCustomToken requires a custom token from Firebase Admin SDK
        // Since we're storing ID tokens, we need a different approach
        // For now, let's just log and let Firebase's normal persistence handle it
        console.log(
          "🔐 [useAuth] Stored token found, Firebase should restore automatically"
        );
      } catch (error) {
        console.error(
          "❌ [useAuth] Failed to restore auth from stored token:",
          error
        );
      }
    } else if (!storedToken) {
      console.log("🔐 [useAuth] No stored token found");
    } else {
      console.log(
        "🔐 [useAuth] Firebase user already exists, no need to restore"
      );
    }
  };

  // Helper function to restore auth from a handoff custom token cookie
  const restoreAuthFromCustomToken = useCallback(async () => {
    if (user) return;
    try {
      const customToken = readCookie("firebaseCustomToken");
      if (customToken) {
        console.log(
          "🔐 [useAuth] Found handoff custom token cookie, signing in..."
        );
        await signInWithCustomToken(auth, customToken);
        clearCookie("firebaseCustomToken");
        setUser(auth.currentUser);
        setLoading(false);
        console.log("✅ [useAuth] Signed in using handoff custom token.");
        return;
      }
    } catch (error) {
      console.error("❌ [useAuth] Handoff custom token sign-in failed:", error);
      clearCookie("firebaseCustomToken");
      router.replace("/login?reason=session-lost");

      return;
    }
    console.log("🔐 [useAuth] No handoff custom token cookie present.");
  }, [router, user]);

  useEffectOnce(() => {
    console.log(
      "🔐 [useAuth] Setting up auth state listener and restoration..."
    );

    // Start auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setLoading(false);
        return;
      }
      // No user yet; attempt restoration flows
      await restoreAuthFromCookie();
      await restoreAuthFromCustomToken();
      // If still no user after attempts, end loading to allow public pages to render
      setLoading(false);
    });

    return () => unsubscribe();
  }, [restoreAuthFromCustomToken]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    setUser(auth.currentUser);
  };

  const signup = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
    setUser(auth.currentUser);
  };

  const logout = async () => {
    await signOut(auth);
    localStorage.removeItem("schoolSession");
    setUser(null);
  };

  const getIdToken = async () => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken();
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
    setUser(auth.currentUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        getIdToken,
        loginWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
